/**
 * 서버 오류 코드 → 사용자 언어 문구.
 *
 * BE 는 사용자에게 보여줄 4xx 오류에 문장 대신 `code`(RFC 9457 확장 멤버)를 싣고, FE 는 i18n 키 `errors.<CODE>` 로
 * 번역한다. 번역 키가 없으면 HTTP 상태별 문구(`errors.status.<status>`), 그것도 없으면 서버가 함께 보낸 대체 문구(title)를 쓴다. 코드 단일 출처: BE `common/exception/ErrorCodes`.
 */
import { ApiError, type ApiErrorParams } from "./api-client";
import i18next from "./i18n";
import { resolveLabel } from "./i18n/resolveLabel";

/** BE `ErrorCodes` 와 같은 값. FE 가 코드로 분기 처리할 때만 여기에 추가한다. */
export const ERROR_CODES = {
  RELATION_DUPLICATE: "RELATION_DUPLICATE",
  ACCOUNT_TEMP_LOCKED: "ACCOUNT_TEMP_LOCKED",
  ITEM_STALE: "ITEM_STALE",
  AUTH_SERVER_UNAVAILABLE: "AUTH_SERVER_UNAVAILABLE",
} as const;

/** 오류가 특정 서버 오류 코드인지 판별. */
export function isApiErrorCode(err: unknown, code: string): boolean {
  return err instanceof ApiError && err.code === code;
}

/**
 * 다른 화면·사용자가 먼저 처리해 지금 화면이 낡은 상태(409 Conflict) — 이미 지난 단계 전이, 이미 있는 참가자·인증서 등.
 * 호출부는 팝업을 닫고 최신 데이터로 새로고침한다.
 */
export function isStaleConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}

/**
 * 보간값의 다국어 맵은 현재 언어 라벨 문자열로 풀어 i18next 보간에 넘긴다(맵 그대로면 `[object Object]` 가 됨).
 * 맵에 현재 언어가 없으면 ko → 맵의 첫 값으로 폴백(빈 칸 방지).
 */
function resolveParams(params: ApiErrorParams, lang: string): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(params).map(([k, v]) => [
      k,
      typeof v === "object" ? resolveLabel(v.ko ?? Object.values(v)[0] ?? "", v, lang) : v,
    ]),
  );
}

/**
 * 동시편집 충돌 — 화면이 본 뒤 다른 사용자가 이 항목(또는 기준 항목)을 먼저 바꿈(서버 409 ITEM_STALE).
 * 중복 참가자·결재라인 잠금 같은 다른 409 와 구분한다. 처리 = 입력 폐기 + 새로고침(`notifyItemStale`).
 */
export function isItemStale(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === ERROR_CODES.ITEM_STALE;
}

/**
 * 사용자에게 보여줄 오류 문구. 우선순위:
 * 1) 코드(UNKNOWN 제외)의 `errors.<CODE>` 번역 — ApiError.params(숫자·문자열·다국어 맵)를 보간값으로 씀
 * 2) HTTP 상태의 `errors.status.<status>` 번역
 * 3) 서버 대체 문구(message) — ApiError 가 아닌 오류도 여기로 온다
 */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const codeKey = `errors.${err.code}`;
    if (err.code !== "UNKNOWN" && i18next.exists(codeKey)) {
      return i18next.t(codeKey, resolveParams(err.params, i18next.language));
    }
    const statusKey = `errors.status.${err.status}`;
    if (i18next.exists(statusKey)) return i18next.t(statusKey);
  }
  return err instanceof Error ? err.message : String(err);
}
